import type { FollowListKind } from './friends';

/**
 * Вкладки экрана связей. Значение живёт в адресе, поэтому это часть публичного контракта URL.
 *
 * Четыре множества, и они не пересекаются: взаимные, только мои подписки, только подписчики,
 * рекомендации. Человек лежит ровно в одной вкладке — до этого он мог быть сразу в двух, и
 * приходилось чипом объяснять, кто на вкладке «настоящий».
 *
 * Заблокированных здесь нет: их список живёт в настройках (`/dashboard/settings/blocked`). Пятая
 * вкладка стояла один день (28.08.2026) и уехала — редкий экран про собственные решения, а не про
 * круг, и в 358px полоса его уже не вмещала.
 */
export const FRIENDS_TABS = ['friends', 'following', 'followers', 'recommended'] as const;

export type FriendsTab = (typeof FRIENDS_TABS)[number];

export const DEFAULT_FRIENDS_TAB: FriendsTab = 'friends';

export const parseFriendsTab = (value: unknown): FriendsTab =>
  FRIENDS_TABS.includes(value as FriendsTab) ? (value as FriendsTab) : DEFAULT_FRIENDS_TAB;

/** Каждой вкладке — свой серверный список. Пересечений нет, поэтому и чипа «взаимные» больше нет. */
export const listKindFor = (tab: FriendsTab): FollowListKind => {
  if (tab === 'followers') {
    return 'newFollowers';
  }
  if (tab === 'following') {
    return 'followingOnly';
  }
  return 'friends';
};

/**
 * Списки, которые этот экран действительно читает, — и, значит, ровно те, что обязана обновить
 * подписка или отписка.
 *
 * Набор ВЫВЕДЕН из `listKindFor`, а не выписан руками, и это не аккуратность ради аккуратности.
 * Выписанный руками он уже разошёлся: в нём стояли `following` и `followers`, которых экран не
 * читает, и не было `followingOnly`, который он читает на каждой вкладке «Подписки». Промах
 * молчаливый — подписка честно инвалидировала три несуществующих на экране запроса, а вкладка
 * «Подписки» не обновлялась никогда.
 */
export const ALL_LIST_KINDS: FollowListKind[] = [...new Set(FRIENDS_TABS.map(listKindFor))];
